import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import Gantt from 'frappe-gantt';
import type { ScheduleTask, ViewMode } from '../../types/schedule';
import '../../styles/gantt.css'; // Includes base frappe-gantt styles + custom overrides

export interface GanttTask {
  id: string;
  name: string;
  start: string;
  end: string;
  progress: number;
  dependencies?: string;
  custom_class?: string;
}

interface GanttChartProps {
  tasks: ScheduleTask[];
  viewMode: ViewMode;
  onTaskClick?: (task: ScheduleTask) => void;
  onDateChange?: (taskId: string, start: Date, end: Date) => void;
  onProgressChange?: (taskId: string, progress: number) => void;
  onViewChange?: (mode: string) => void;
  showCriticalPath?: boolean;
  className?: string;
}

// Convert ScheduleTask to frappe-gantt format (pure function)
const convertToGanttTask = (task: ScheduleTask, showCriticalPath: boolean): GanttTask => {
  // Format dependencies as comma-separated WBS codes or IDs
  const dependencies = task.predecessors
    ?.map(p => p.taskId)
    .filter(Boolean)
    .join(', ');

  // Determine custom class based on task properties
  let customClass = '';
  if (task.taskType === 'milestone') {
    customClass = 'milestone';
  } else if (task.taskType === 'summary') {
    customClass = 'summary';
  } else if (showCriticalPath && task.isCritical) {
    customClass = 'critical';
  } else {
    // Priority-based coloring
    switch (task.priority) {
      case 'critical':
        customClass = 'priority-critical';
        break;
      case 'high':
        customClass = 'priority-high';
        break;
      case 'low':
        customClass = 'priority-low';
        break;
      default:
        customClass = '';
    }
  }

  // Add status class
  if (task.status === 'completed') {
    customClass += ' completed';
  } else if (task.status === 'on_hold') {
    customClass += ' on-hold';
  } else if (task.status === 'cancelled') {
    customClass += ' cancelled';
  }

  return {
    id: task._id,
    name: `${task.wbsCode} - ${task.name}`,
    start: new Date(task.plannedStart).toISOString().split('T')[0],
    end: new Date(task.plannedEnd).toISOString().split('T')[0],
    progress: task.percentComplete,
    dependencies,
    custom_class: customClass.trim(),
  };
};

// Map ViewMode to frappe-gantt view mode
const mapViewMode = (mode: ViewMode): string => {
  switch (mode) {
    case 'day':
      return 'Day';
    case 'week':
      return 'Week';
    case 'month':
      return 'Month';
    case 'quarter':
      return 'Year'; // frappe-gantt doesn't have Quarter, use Year instead
    default:
      return 'Week';
  }
};

const GanttChart: React.FC<GanttChartProps> = ({
  tasks,
  viewMode,
  onTaskClick,
  onDateChange,
  onProgressChange,
  onViewChange,
  showCriticalPath = true,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const ganttRef = useRef<Gantt | null>(null);
  const tasksMapRef = useRef<Map<string, ScheduleTask>>(new Map());

  // Store callbacks in refs to avoid re-initialization on callback changes
  const onTaskClickRef = useRef(onTaskClick);
  const onDateChangeRef = useRef(onDateChange);
  const onProgressChangeRef = useRef(onProgressChange);
  const onViewChangeRef = useRef(onViewChange);

  // Update refs when callbacks change
  useEffect(() => {
    onTaskClickRef.current = onTaskClick;
    onDateChangeRef.current = onDateChange;
    onProgressChangeRef.current = onProgressChange;
    onViewChangeRef.current = onViewChange;
  }, [onTaskClick, onDateChange, onProgressChange, onViewChange]);

  // Build tasks map for quick lookup
  useEffect(() => {
    const map = new Map<string, ScheduleTask>();
    tasks.forEach(task => map.set(task._id, task));
    tasksMapRef.current = map;
  }, [tasks]);

  // Memoize converted gantt tasks to avoid unnecessary recalculations
  const ganttTasks = useMemo(() => {
    return tasks.map(task => convertToGanttTask(task, showCriticalPath));
  }, [tasks, showCriticalPath]);

  // Custom popup HTML generator
  const getCustomPopupHtml = useCallback((ganttTask: GanttTask): string => {
    const task = tasksMapRef.current.get(ganttTask.id);
    if (!task) return '';

    const startDate = new Date(task.plannedStart).toLocaleDateString();
    const endDate = new Date(task.plannedEnd).toLocaleDateString();

    return `
      <div class="gantt-popup">
        <div class="gantt-popup-header">
          <span class="gantt-popup-wbs">${task.wbsCode}</span>
          <span class="gantt-popup-type gantt-popup-type-${task.taskType}">${task.taskType}</span>
        </div>
        <div class="gantt-popup-title">${task.name}</div>
        <div class="gantt-popup-dates">
          <span>${startDate}</span>
          <span>→</span>
          <span>${endDate}</span>
        </div>
        <div class="gantt-popup-progress">
          <div class="gantt-popup-progress-bar" style="width: ${task.percentComplete}%"></div>
          <span>${task.percentComplete}%</span>
        </div>
        <div class="gantt-popup-meta">
          <span class="gantt-popup-status gantt-popup-status-${task.status}">${task.status.replace('_', ' ')}</span>
          <span class="gantt-popup-priority gantt-popup-priority-${task.priority}">${task.priority}</span>
          ${task.isCritical ? '<span class="gantt-popup-critical">Critical Path</span>' : ''}
        </div>
      </div>
    `;
  }, []);

  // Initialize Gantt chart
  useEffect(() => {
    if (!containerRef.current || ganttTasks.length === 0) return;

    // CRITICAL: Clear any existing content before initializing
    // frappe-gantt doesn't have a destroy method, so we need to manually clean up
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    ganttRef.current = null;

    // Initialize Gantt
    ganttRef.current = new Gantt(containerRef.current, ganttTasks, {
      view_mode: mapViewMode(viewMode),
      date_format: 'YYYY-MM-DD',
      popup_trigger: 'mouseover',
      custom_popup_html: getCustomPopupHtml,
      on_click: (ganttTask: GanttTask) => {
        const task = tasksMapRef.current.get(ganttTask.id);
        if (task && onTaskClickRef.current) {
          onTaskClickRef.current(task);
        }
      },
      on_date_change: (ganttTask: GanttTask, start: Date, end: Date) => {
        if (onDateChangeRef.current) {
          onDateChangeRef.current(ganttTask.id, start, end);
        }
      },
      on_progress_change: (ganttTask: GanttTask, progress: number) => {
        if (onProgressChangeRef.current) {
          onProgressChangeRef.current(ganttTask.id, progress);
        }
      },
      on_view_change: (mode: string) => {
        if (onViewChangeRef.current) {
          onViewChangeRef.current(mode);
        }
      },
    });

    return () => {
      // Cleanup: clear container and reset ref
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      ganttRef.current = null;
    };
  }, [ganttTasks, viewMode, getCustomPopupHtml]); // Only re-initialize when tasks or viewMode changes

  // Update view mode when it changes (if gantt already exists)
  useEffect(() => {
    if (ganttRef.current) {
      ganttRef.current.change_view_mode(mapViewMode(viewMode));
    }
  }, [viewMode]);

  // Show empty state if no tasks
  if (tasks.length === 0) {
    return (
      <div
        className={`flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200 ${className}`}
      >
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No schedule tasks</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating your first task.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`gantt-chart-wrapper ${className}`}>
      <div ref={containerRef} className="gantt-container" />
    </div>
  );
};

export default GanttChart;
